<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, PackageCheck, Pencil, RefreshCw, Tag, Trash2, X } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, formatDisplayDateTime, toApiDateTime } from '@yuksalish/ui'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import {
  LOT_QUALITY_STATUSES,
  baseUomName,
  isOpenPurchaseOrder,
  lotLabel,
  purchaseOrderStatusClass,
  receivedQuantity,
  remainingQuantity,
  uniqueOrderItemId,
} from '../types/procurement'
import type {
  AgentOption,
  LotQualityStatus,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderReceipt,
  ReceivePurchaseOrderItem,
} from '../types/procurement'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const api = useProcurementApi()

const orderId = computed(() => Number(route.params.id ?? 0))
const canUpdate = computed(() => (auth.user?.permissions ?? []).includes('purchase_orders.update'))
const canDelete = computed(() => (auth.user?.permissions ?? []).includes('purchase_orders.delete'))

const order = ref<PurchaseOrder | null>(null)
const warehouses = ref<AgentOption[]>([])
const receipts = ref<PurchaseOrderReceipt[]>([])
const loading = ref(true)
const error = ref('')

const actionError = ref('')
const actionMessage = ref('')

const showReceive = ref(false)
const receiving = ref(false)
const receiveAll = ref(true)
const receiveWarehouse = ref('')
const receiveQuantities = reactive<Record<number, number | string>>({})

/**
 * Optional lot/batch metadata per product, keyed by product id (received
 * quantities are tracked per product by the backend).
 */
interface LotDraft {
  lot_number: string
  supplier_batch_number: string
  production_batch_number: string
  /** `datetime-local` value; converted to `Y-m-d H:i:s` on submit. */
  manufactured_at: string
  /** `date` value (`Y-m-d`). */
  expires_at: string
  quality_status: '' | LotQualityStatus
}

const lots = reactive<Record<number, LotDraft>>({})
const openLotLines = ref<number[]>([])

const qualityStatuses = LOT_QUALITY_STATUSES

function lotFor(productId: number): LotDraft {
  if (!lots[productId]) {
    lots[productId] = {
      lot_number: '',
      supplier_batch_number: '',
      production_batch_number: '',
      manufactured_at: '',
      expires_at: '',
      quality_status: '',
    }
  }

  return lots[productId]
}

function isLotOpen(productId: number): boolean {
  return openLotLines.value.includes(productId)
}

function toggleLot(productId: number) {
  openLotLines.value = isLotOpen(productId)
    ? openLotLines.value.filter((id) => id !== productId)
    : [...openLotLines.value, productId]
}

/** True when the operator filled anything in the line's lot panel. */
function hasLotData(productId: number): boolean {
  const draft = lots[productId]
  if (!draft) return false

  return (
    lotLabel(draft) !== ''
    || draft.manufactured_at !== ''
    || draft.expires_at !== ''
    || draft.quality_status !== ''
  )
}

const showDelete = ref(false)
const deleting = ref(false)

const canReceive = computed(() => canUpdate.value && order.value !== null && isOpenPurchaseOrder(order.value.status))

const items = computed(() => order.value?.items ?? [])

/** Main (base) UOM name per product, resolved from `GET /products/{id}/uoms`. */
const uomByProduct = ref<Record<number, string>>({})

function uomFor(productId: number): string {
  return uomByProduct.value[productId] ?? ''
}

/** `warehouse_receipt #91` — the document a purchase movement came from. */
function receiptDocumentLabel(receipt: PurchaseOrderReceipt): string {
  if (!receipt.source_document_type) return '—'
  return `${receipt.source_document_type} #${receipt.source_document_id ?? '—'}`
}

async function loadProductUoms(lines: readonly PurchaseOrderItem[]) {
  const productIds = [...new Set(lines.map((line) => line.product_id))]

  const resolved = await Promise.all(
    productIds.map(async (productId) => {
      try {
        const { unit, items: uoms } = await api.getProductUoms(productId)
        return [productId, baseUomName(uoms, unit)] as const
      } catch {
        return [productId, ''] as const
      }
    }),
  )

  const map: Record<number, string> = {}
  for (const [productId, name] of resolved) {
    if (name) map[productId] = name
  }

  uomByProduct.value = map
}

function money(value: number, currency: string | null | undefined): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

async function loadReceipts() {
  try {
    receipts.value = await api.listPurchaseOrderReceipts(orderId.value)
  } catch {
    receipts.value = []
  }
}

async function load() {
  loading.value = true
  error.value = ''
  actionError.value = ''
  try {
    const [detail, warehouseList] = await Promise.all([
      api.getPurchaseOrder(orderId.value),
      api.listWarehouses().catch(() => []),
    ])
    order.value = detail
    warehouses.value = warehouseList
    void loadProductUoms(detail.items ?? [])
    void loadReceipts()
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function openReceive() {
  actionError.value = ''
  actionMessage.value = ''
  receiveAll.value = true
  receiveWarehouse.value = order.value?.destination_warehouse_agent_id
    ? String(order.value.destination_warehouse_agent_id)
    : ''
  openLotLines.value = []
  for (const item of items.value) {
    // Default to what is still outstanding — the backend rejects anything above it.
    receiveQuantities[item.product_id] = remainingQuantity(item)
    lotFor(item.product_id)
  }
  showReceive.value = true
}

async function submitReceive() {
  if (!order.value) return

  actionError.value = ''

  let receiveItems: ReceivePurchaseOrderItem[] = []

  if (!receiveAll.value) {
    const lines = items.value.map((item) => ({
      item,
      quantity: Number(receiveQuantities[item.product_id] ?? 0),
    }))

    const excessive = lines.find((line) => line.quantity > remainingQuantity(line.item))
    if (excessive) {
      actionError.value = t('procurement.receive.validationRemaining', {
        product: excessive.item.product_name || `#${excessive.item.product_id}`,
        quantity: remainingQuantity(excessive.item),
      })
      return
    }

    receiveItems = lines
      .filter((line) => line.quantity > 0)
      .map((line) => {
        const productId = line.item.product_id
        const payload: ReceivePurchaseOrderItem = { product_id: productId, quantity: line.quantity }

        // The backend links the receipt line automatically when the product
        // appears once on the order; repeated products have to stay unlinked.
        const orderItemId = uniqueOrderItemId(items.value, productId)
        if (orderItemId !== null) payload.purchase_order_item_id = orderItemId

        const draft = lots[productId]
        if (draft) {
          if (draft.lot_number.trim()) payload.lot_number = draft.lot_number.trim()
          if (draft.supplier_batch_number.trim()) payload.supplier_batch_number = draft.supplier_batch_number.trim()
          if (draft.production_batch_number.trim()) payload.production_batch_number = draft.production_batch_number.trim()
          if (draft.manufactured_at) payload.manufactured_at = toApiDateTime(draft.manufactured_at)
          if (draft.expires_at) payload.expires_at = draft.expires_at
          if (draft.quality_status) payload.quality_status = draft.quality_status
        }

        return payload
      })

    if (receiveItems.length === 0) {
      actionError.value = t('procurement.receive.validationEmpty')
      return
    }
  }

  receiving.value = true
  try {
    const payload = receiveAll.value
      ? { destination_warehouse_agent_id: receiveWarehouse.value ? Number(receiveWarehouse.value) : undefined }
      : {
          destination_warehouse_agent_id: receiveWarehouse.value ? Number(receiveWarehouse.value) : undefined,
          items: receiveItems,
        }

    order.value = await api.receivePurchaseOrder(order.value.id, payload)
    showReceive.value = false
    actionMessage.value = order.value.receipt?.document_number
      ? t('procurement.receive.successWithDocument', { document: order.value.receipt.document_number })
      : t('procurement.receive.success')
    void loadReceipts()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    receiving.value = false
  }
}

async function confirmDelete() {
  if (!order.value) return

  deleting.value = true
  actionError.value = ''
  try {
    await api.deletePurchaseOrder(order.value.id)
    await router.push('/purchase-orders')
  } catch (cause) {
    actionError.value = formatApiError(cause)
    deleting.value = false
    showDelete.value = false
  }
}

onMounted(load)
</script>

<template>
  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <RouterLink class="btn-primary" to="/purchase-orders">
      <ArrowLeft :size="15" aria-hidden="true" />
      {{ t('procurement.order.back') }}
    </RouterLink>
  </div>

  <template v-else-if="order">
    <div class="page-heading page-heading--actions">
      <div>
        <RouterLink class="back" to="/purchase-orders">
          <ArrowLeft :size="14" aria-hidden="true" />
          {{ t('procurement.order.back') }}
        </RouterLink>
        <h2>#{{ order.id }} · {{ order.supplier_name || '—' }}</h2>
      </div>
      <div class="profile-actions">
        <RouterLink v-if="canUpdate" class="profile-action" :to="`/purchase-orders/${order.id}/edit`">
          <Pencil :size="15" aria-hidden="true" />
          {{ t('procurement.order.edit') }}
        </RouterLink>
        <button v-if="canReceive" class="profile-action profile-action--primary" type="button" @click="openReceive">
          <PackageCheck :size="15" aria-hidden="true" />
          {{ t('procurement.order.receive') }}
        </button>
        <button v-if="canDelete" class="profile-action danger" type="button" @click="showDelete = true">
          <Trash2 :size="15" aria-hidden="true" />
          {{ t('procurement.order.delete') }}
        </button>
      </div>
    </div>

    <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
    <p v-if="actionError && !showReceive" class="error-inline">{{ actionError }}</p>

    <div class="detail-grid">
      <div class="panel">
        <h3>{{ t('procurement.order.summary') }}</h3>
        <dl class="detail-list">
          <div class="detail-row">
            <dt>{{ t('procurement.order.status') }}</dt>
            <dd>
              <span :class="['status-badge', purchaseOrderStatusClass(order.status)]">
                {{ t(`procurement.status.${order.status}`) }}
              </span>
            </dd>
          </div>
          <div class="detail-row"><dt>{{ t('procurement.order.supplier') }}</dt><dd>{{ order.supplier_name || '—' }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.order.warehouse') }}</dt><dd>{{ order.destination_warehouse_name || '—' }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.order.requester') }}</dt><dd>{{ order.requester_user_name || '—' }}</dd></div>
          <div class="detail-row">
            <dt>{{ t('procurement.order.currency') }}</dt>
            <dd>{{ order.currency_code || '—' }} · {{ t('procurement.order.exchangeRate') }} {{ order.exchange_rate }}</dd>
          </div>
          <div class="detail-row">
            <dt>{{ t('procurement.order.expected') }}</dt>
            <dd>{{ order.expected_date ? formatDisplayDate(order.expected_date) : '—' }}</dd>
          </div>
          <div class="detail-row"><dt>{{ t('procurement.order.created') }}</dt><dd>{{ formatDisplayDateTime(order.created_at) }}</dd></div>
          <div class="detail-row"><dt>{{ t('procurement.order.notes') }}</dt><dd>{{ order.notes || '—' }}</dd></div>
        </dl>
      </div>

      <div class="panel">
        <h3>{{ t('procurement.order.items') }}</h3>
        <table v-if="items.length" class="data-table">
          <thead>
            <tr>
              <th>{{ t('procurement.order.product') }}</th>
              <th class="cell-qty">{{ t('procurement.order.quantity') }}</th>
              <th class="cell-qty">{{ t('procurement.order.received') }}</th>
              <th class="cell-qty">{{ t('procurement.order.remaining') }}</th>
              <th class="cell-qty">{{ t('procurement.order.price') }}</th>
              <th class="cell-qty">{{ t('procurement.order.lineTotal') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td class="cell-name">
                {{ item.product_name || `#${item.product_id}` }}
                <small class="cell-sub">
                  {{ item.sku || '—' }}
                  <template v-if="uomFor(item.product_id)"> · {{ uomFor(item.product_id) }}</template>
                </small>
              </td>
              <td class="cell-qty">
                {{ item.quantity }}<small v-if="uomFor(item.product_id)" class="cell-uom">{{ uomFor(item.product_id) }}</small>
              </td>
              <td class="cell-qty">{{ receivedQuantity(item) }}</td>
              <td class="cell-qty">{{ remainingQuantity(item) }}</td>
              <td class="cell-qty">{{ money(item.price, order.currency_code) }}</td>
              <td class="cell-qty">{{ money(item.total, order.currency_code) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5" class="cell-qty"><strong>{{ t('procurement.order.grandTotal') }}</strong></td>
              <td class="cell-qty"><strong>{{ money(order.total_amount, order.currency_code) }}</strong></td>
            </tr>
          </tfoot>
        </table>
        <p v-else class="empty-inline">{{ t('procurement.order.noItems') }}</p>
      </div>
    </div>

    <!-- Receipts: inbound purchase movements booked against this order. -->
    <div class="panel receipts-panel">
      <div class="panel-header">
        <h3>{{ t('procurement.receipts.title') }}</h3>
        <span class="empty-inline">{{ t('procurement.receipts.count', { count: receipts.length }) }}</span>
      </div>
      <p class="form-note">{{ t('procurement.receipts.hint') }}</p>
      <table v-if="receipts.length" class="data-table">
        <thead>
          <tr>
            <th>{{ t('procurement.receipts.date') }}</th>
            <th>{{ t('procurement.receipts.item') }}</th>
            <th class="cell-qty">{{ t('procurement.receipts.quantity') }}</th>
            <th>{{ t('procurement.receipts.warehouse') }}</th>
            <th>{{ t('procurement.receipts.lot') }}</th>
            <th>{{ t('procurement.receipts.document') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="receipt in receipts" :key="receipt.id">
            <td>{{ formatDisplayDateTime(receipt.created_at) }}</td>
            <td class="cell-name">
              {{ receipt.product_name || `#${receipt.product_id}` }}
              <small class="cell-sub">
                {{ receipt.sku || '—' }}
                <template v-if="uomFor(receipt.product_id)"> · {{ uomFor(receipt.product_id) }}</template>
              </small>
            </td>
            <td class="cell-qty">
              {{ receipt.quantity }}<small v-if="uomFor(receipt.product_id)" class="cell-uom">{{ uomFor(receipt.product_id) }}</small>
            </td>
            <td>{{ receipt.warehouse_name || '—' }}</td>
            <td class="cell-code">{{ lotLabel(receipt) || t('procurement.receipts.noLot') }}</td>
            <td class="cell-code">{{ receiptDocumentLabel(receipt) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="empty-inline">{{ t('procurement.receipts.empty') }}</p>
    </div>

    <!-- Receive dialog -->
    <div v-if="showReceive" class="modal-overlay" @click.self="showReceive = false">
      <div class="modal modal--wide">
        <h3>{{ t('procurement.receive.title') }}</h3>
        <p class="form-note">{{ t('procurement.receive.description') }}</p>

        <div class="field">
          <label>{{ t('procurement.receive.warehouse') }}</label>
          <select v-model="receiveWarehouse" class="filter-select">
            <option value="">{{ t('procurement.common.selectPlaceholder') }}</option>
            <option v-for="warehouse in warehouses" :key="warehouse.id" :value="String(warehouse.id)">
              {{ warehouse.name }}
            </option>
          </select>
          <small>{{ t('procurement.receive.warehouseHint') }}</small>
        </div>

        <label class="check-field">
          <input v-model="receiveAll" type="checkbox">
          <span>{{ t('procurement.receive.receiveAll') }}</span>
        </label>
        <p v-if="receiveAll" class="form-note">{{ t('procurement.receive.receiveAllHint') }}</p>
        <p v-if="receiveAll" class="form-note">{{ t('procurement.receive.lotNeedsQuantities') }}</p>

        <table class="data-table receive-table">
          <thead>
            <tr>
              <th>{{ t('procurement.order.product') }}</th>
              <th class="cell-qty">{{ t('procurement.receive.ordered') }}</th>
              <th class="cell-qty">{{ t('procurement.receive.received') }}</th>
              <th class="cell-qty">{{ t('procurement.receive.remaining') }}</th>
              <th v-if="!receiveAll" class="cell-qty">{{ t('procurement.receive.now') }}</th>
              <th v-if="!receiveAll">{{ t('procurement.receive.lotTitle') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in items" :key="item.product_id">
              <tr>
                <td class="cell-name">
                  {{ item.product_name || `#${item.product_id}` }}
                  <small v-if="uomFor(item.product_id)" class="cell-sub">{{ uomFor(item.product_id) }}</small>
                </td>
                <td class="cell-qty">{{ item.quantity }}</td>
                <td class="cell-qty">{{ receivedQuantity(item) }}</td>
                <td class="cell-qty">{{ remainingQuantity(item) }}</td>
                <td v-if="!receiveAll" class="cell-qty">
                  <input
                    v-model="receiveQuantities[item.product_id]"
                    class="receive-input"
                    type="number"
                    min="0"
                    :max="remainingQuantity(item)"
                    step="0.0001"
                  >
                </td>
                <td v-if="!receiveAll">
                  <button
                    class="lot-toggle"
                    :class="{ 'lot-toggle--set': hasLotData(item.product_id) }"
                    type="button"
                    @click="toggleLot(item.product_id)"
                  >
                    <Tag :size="13" aria-hidden="true" />
                    {{ hasLotData(item.product_id) ? lotLabel(lotFor(item.product_id)) : t('procurement.receive.lotButton') }}
                  </button>
                </td>
              </tr>
              <tr v-if="!receiveAll && isLotOpen(item.product_id)" class="receive-lot-row">
                <td :colspan="6">
                  <div class="lot-grid">
                    <div class="field">
                      <label>{{ t('procurement.receive.lotNumber') }}</label>
                      <input v-model="lotFor(item.product_id).lot_number" type="text" :placeholder="t('procurement.receive.lotNumberPlaceholder')">
                    </div>
                    <div class="field">
                      <label>{{ t('procurement.receive.supplierBatch') }}</label>
                      <input v-model="lotFor(item.product_id).supplier_batch_number" type="text">
                    </div>
                    <div class="field">
                      <label>{{ t('procurement.receive.productionBatch') }}</label>
                      <input v-model="lotFor(item.product_id).production_batch_number" type="text">
                    </div>
                    <div class="field">
                      <label>{{ t('procurement.receive.manufacturedAt') }}</label>
                      <input v-model="lotFor(item.product_id).manufactured_at" type="datetime-local">
                    </div>
                    <div class="field">
                      <label>{{ t('procurement.receive.expiresAt') }}</label>
                      <input v-model="lotFor(item.product_id).expires_at" type="date">
                    </div>
                    <div class="field">
                      <label>{{ t('procurement.receive.qualityStatus') }}</label>
                      <select v-model="lotFor(item.product_id).quality_status">
                        <option value="">{{ t('procurement.common.selectPlaceholder') }}</option>
                        <option v-for="status in qualityStatuses" :key="status" :value="status">
                          {{ t(`procurement.lotQuality.${status}`) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <small class="form-note">{{ t('procurement.receive.lotHint') }}</small>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <p v-if="!receiveAll" class="form-note">{{ t('procurement.receive.specificHint') }}</p>

        <small v-if="actionError" class="error-inline">{{ actionError }}</small>

        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="showReceive = false">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="receiving || !receiveWarehouse" @click="submitReceive">
            <PackageCheck :size="15" aria-hidden="true" />
            {{ receiving ? t('procurement.receive.submitting') : t('procurement.receive.submit') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <div v-if="showDelete" class="modal-overlay" @click.self="showDelete = false">
      <div class="modal">
        <h3>{{ t('procurement.order.deleteTitle') }}</h3>
        <p class="form-note">{{ t('procurement.order.deleteConfirm') }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="showDelete = false">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="deleting" @click="confirmDelete">
            <Trash2 :size="15" aria-hidden="true" />
            {{ deleting ? t('procurement.common.saving') : t('procurement.common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </template>

  <div v-else class="panel">
    <span class="empty-inline">{{ t('procurement.order.notFound') }}</span>
  </div>
</template>
